import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";
import { useToast } from "@/hooks/use-toast";
import { useConfirmation } from "@/hooks/use-confirmation";
import { ItemTypeConfig } from "@/pages/unified-items/configs/types";
import { deleteLogicFlow, createLogicFlow } from "@/lib/stores/logicFlowStore";
import { getCurrentUser } from "@/lib/stores/unifiedUserStore";

export interface UseFlowsHandlersProps {
  data: any[];
  selectedGame: any;
  config: ItemTypeConfig;
}

export function useFlowsHandlers({
  data,
  selectedGame,
  config,
}: UseFlowsHandlersProps) {
  const { toast } = useToast();
  const { confirmation } = useConfirmation();
  const navigate = useNavigate();

  const handleDelete = useCallback(
    async (id: string, gameId: string) => {
      const item = data.find((item: any) => item.id === id);
      const itemName = item?.name || "this flow";

      const confirmDialog = confirmation({
        title: "Delete Logic Flow",
        description: `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
        confirmText: "Delete",
        cancelText: "Cancel",
        variant: "destructive",
        onConfirm: async () => {
          try {
            await deleteLogicFlow(id, gameId, toast);
            confirmDialog.dismiss();
          } catch (error) {
            console.error("Error deleting logic flow:", error);
            confirmDialog.dismiss();
            toast({
              title: "Error",
              description: "Failed to delete logic flow",
              variant: "destructive",
            });
          }
        },
        onCancel: () => {
          confirmDialog.dismiss();
        },
      });
    },
    [data, confirmation, toast]
  );

  const handleCardClick = useCallback(
    (card: CardData) => {
      navigate(`/logic/${card.id}`, { state: { itemName: card.title } });
    },
    [navigate]
  );

  const handleAdd = useCallback(async () => {
    if (!selectedGame?.id) {
      toast({
        title: "Error",
        description: "Please select a game first",
        variant: "destructive",
      });
      return;
    }

    try {
      const currentUser = getCurrentUser();
      const newFlow = await createLogicFlow({
        game_id: selectedGame.id,
        name: `New Logic Flow`,
        description: 'Logic flow',
        data: {
          nodes: [],
          edges: [],
          viewport: { x: 0, y: 0, zoom: 1 },
        },
        owner: currentUser?.id || null,
      }, toast);

      // Navigate to the new flow
      navigate(`/logic/${newFlow.id}`);
    } catch (error) {
      console.error("Error creating logic flow:", error);
    }
  }, [selectedGame, toast, navigate]);

  return {
    handleDelete,
    handleCardClick,
    handleAdd,
  };
}

